import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Stack,
  Card,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { selectUserDetail } from "../../redux/slice/userSlice";
import { httpPost } from "../../services/commonService";
import urlConstant from "../../constants/urlConstants";
import { AuthenticationContext } from "@cloudmlm/cloudmlm-auth";
import dataConstant from "../../constants/dataConstants";
import SnackState from "../../shared-components/snackbar/snackState";
import CommonSnackBar from "../../shared-components/snackbar/commonSnackbar";

export default function Omni() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authenticationState = useContext(AuthenticationContext);
  const userDetail = useSelector(selectUserDetail);
  const [snack, closeSnack, showSnackbar] = SnackState();
  const [state, setState] = useState({
    isPopupOpen: false,
    refURL: "",
    omniPackageDetails: [],
    omniRedirectURL: "",
    isOmniDiamondUpgrade: false,
    showSetupButton: false,
    isEnrollWithOmni: "",
  });
  const isOmniAlreadyEnrolled = userDetail?.PurchasePackageDetails?.IsOmniDiamondEnrolled
    || userDetail?.PurchasePackageDetails?.IsOmniPlatinumEnrolled;

  const fetchOmniUserDetails = async () => {
    try {
      const requestBody = {
        AcctID: userDetail?.UserAccountDetails?.AcctID
      };
      const response = await httpPost(
        urlConstant.Omni.GetOmniUserDetails,
        requestBody,
        true,
        true,
        authenticationState.accessToken,
        authenticationState.impersonateEmail
      );
      if (response?.Status === dataConstant.APIResponseStatus.Success) {
        setState((prevState) => ({
          ...prevState,
          refURL: userDetail?.UserAccountDetails?.DirectSponsorDetails?.DSReplicationSiteName,
          omniPackageDetails: response?.Values?.UpgradePackageDetails,
          omniRedirectURL: response?.Values?.OmniRedirectURL,
          isOmniDiamondUpgrade: response?.Values?.IsOmniDiamondUpgrade,
          isEnrollWithOmni: response?.Values?.IsEnrollWithOmni,
          showSetupButton: response?.Values?.OmniUserID === null ||response?.Values?.OmniRedirectURL === null,
        }));
      }
    } catch (err) {
      console.error("Failed to fetch Omni user details:", err);
    }
  };

  useEffect(() => {
    if (userDetail?.UserAccountDetails?.AcctID) {
      fetchOmniUserDetails();
    }
  }, [userDetail?.UserAccountDetails?.AcctID]);

  const closePopup = () => {
    setState((prevState) => ({
      ...prevState,
      isPopupOpen: false,
    }));
  };

  const enrollOmniUser = async () => {
    try {
      const requestBody = {
        AcctID: userDetail?.UserAccountDetails?.AcctID,
      };

      const response = await httpPost(
        urlConstant.Omni.OmniUserCreate,
        requestBody,
        true,
        true,
        authenticationState.accessToken,
        authenticationState.impersonateEmail
      );

      if (response?.Status === dataConstant.APIResponseStatus.Success) {
        showSnackbar("Omni signup successfully.", "success");
      } else {
        showSnackbar("Failed to Omni signup ", "error");
      }
    } catch (err) {
      console.error("Failed to enroll Omni user:", err);
    }
  };

  return (
    <>
      <Box
        sx={{
          backgroundColor: "#000",
          color: "#fff",
          textAlign: "center",
          py: 2,
          borderRadius: '20px',
          overflow: 'hidden',
        }}
      >
        {/* 🔹 Top Row: Logo (Left) + Button (Right) */}
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: { xs: 2, md: 3 },
            mb: { xs: 2, md: 2 },
          }}
        >
          {/* Logo */}
          <Grid item>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                cursor: "pointer",
              }}
            >
              <Box
                component="img"
                src={`${process.env.REACT_APP_BLOB_URL}/backoffice-assets/OmniLogo.svg`}
                alt="Omni Logo"
                sx={{ height: 30 }}
              />
              <Box
                component="span"
                sx={{
                  fontFamily: "Arial, sans-serif",
                  fontSize: "24px",
                  fontWeight: 600,
                  color: "white",
                  letterSpacing: "-0.5px",
                }}
              >
                {t("Omnii.Omni")}
              </Box>
            </Box>
          </Grid>

          <Stack direction="row" spacing={2}>
            {/* Button */}
            {state.isEnrollWithOmni === false && (
              <Grid item>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#fff",
                    color: "#000",
                    fontWeight: 600,
                    borderRadius: "30px",
                    px: 2,
                    py: 1,
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "#f2f2f2",
                    },
                  }}
                  onClick={() => {
                    setState((prevState) => ({
                      ...prevState,
                      isPopupOpen: true
                    }))
                  }}
                >
                  {t("Omnii.GetOmni")} →
                </Button>
              </Grid>
            )}

            {state.isOmniDiamondUpgrade && (
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#fff",
                    color: "#000",
                    fontWeight: 600,
                    borderRadius: "30px",
                    px: 2,
                    py: 1,
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "#f2f2f2",
                    },
                  }}
                  onClick={() =>
                    navigate(
                      `${state.omniPackageDetails[0].RedirectUrl}${state.refURL}`,
                      {
                        state: { upgrade: true },
                      }
                    )
                  }
                >
                  {t("Common.Upgrade")}
                </Button>

                {state.omniRedirectURL && (
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: "#fff",
                      color: "#000",
                      fontWeight: 600,
                      borderRadius: "30px",
                      px: 2,
                      py: 1,
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: "#f2f2f2",
                      },
                    }}
                    onClick={() => window.open(state.omniRedirectURL, "_blank")}
                  >
                    {t("Omnii.GoToOmni")} →
                  </Button>
                )}
              </Stack>
            )}

            {!state.isOmniDiamondUpgrade && state.omniRedirectURL && (
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#fff",
                  color: "#000",
                  fontWeight: 600,
                  borderRadius: "30px",
                  px: 2,
                  py: 1,
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#f2f2f2",
                  },
                }}
                onClick={() => window.open(state.omniRedirectURL, "_blank")}
              >
                {t("Omnii.GoToOmni")} →
              </Button>
            )}

            {/* {state.showSetupButton && isOmniAlreadyEnrolled && (
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#fff",
                  color: "#000",
                  fontWeight: 600,
                  borderRadius: "30px",
                  px: 2,
                  py: 1,
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#f2f2f2",
                  },
                }}
                onClick={enrollOmniUser}
              >
                {t("Omnii.GetOmni")}
              </Button>
            )} */}
          </Stack>
        </Grid>

        <Grid container spacing={2} p={2} justifyContent="center">
          <Grid item xs={12} position={'relative'} zIndex={1}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: "bold",
                fontSize: { xs: "2rem", md: "3.5rem" },
                lineHeight: 1.2,
                backgroundClip: 'text',
                textFillColor: 'transparent',
                backgroundImage:
                  'linear-gradient(707deg, rgb(255, 255, 255) 0%, rgba(255, 255, 255, 0.7) 100%)',
              }}
            >
              {t("Omnii.Unlocking")}
            </Typography>

            <Typography
              variant="h6"
              sx={{
                mt: 3,
                maxWidth: "800px",
                mx: "auto",
                fontWeight: 400,
                lineHeight: 1.6,
              }}
            >
              {t("Omnii.OmniRemovesTheBarriers")}
            </Typography>

            {/* App Store & Play Store Buttons */}
            <Box
              sx={{
                mt: 3,
                display: "flex",
                justifyContent: "center",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <a href="https://apps.apple.com/us/app/omni-trade-the-world/id6451088745" target="_blank">
                <Box
                  component="img"
                  src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                  alt="App Store"
                  sx={{ height: 50, cursor: "pointer" }}
                />
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.justthrive.omni" target="_blank">
                <Box
                  component="img"
                  src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                  alt="Google Play"
                  sx={{ height: 50, cursor: "pointer" }}
                />
              </a>
            </Box>
          </Grid>

          {/* Video Section with Overlay Image */}
          <Grid item xs={12} width={'100%'}>
            <Box
              className="omni-hero-container"
              sx={{
                mt: 0,
                position: "relative",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {/* Background Video */}
              <Box
                className="omni-video"
                component="video"
                autoPlay
                loop
                muted
                playsInline
                sx={{
                  width: "100%",
                  borderRadius: "20px",
                  boxShadow: "0 0 0px rgba(255,255,255,0.1)",
                }}
              >
                <source
                  src={`${process.env.REACT_APP_BLOB_URL}/backoffice-assets/OmniPageVideo.mp4`}
                  type="video/mp4"
                />
              </Box>

              {/* Overlay Image (Centered) */}
              <Box
                className="omni-mobile-image"
                component="img"
                src={`${process.env.REACT_APP_BLOB_URL}/backoffice-assets/OmniMobile.png`}
                alt="Omni Mobile"
                sx={{
                  width: '100%',
                  maxWidth: '680px',
                  position: 'relative',
                  zIndex: 1,
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Dialog
        open={state.isPopupOpen}
        onClose={closePopup}
        sx={{ borderRadius: "20px", background: "transparent" }}
        maxWidth
      >
        <Stack
          sx={(theme) => ({
            background: theme.palette.common.black,
            borderTopLeftRadius: "20px",
            borderTopRightRadius: "20px",
          })}
        >
          <DialogTitle>{t("Omnii.AvailablePackages")}</DialogTitle>

          <IconButton
            aria-label="close"
            onClick={closePopup}
            sx={(theme) => ({
              position: "absolute",
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
        <DialogContent
          sx={{
            background: "var(--primary-darker)!important",
            border: 0,
            borderBottomLeftRadius: "20px",
            borderBottomRightRadius: "20px",
          }}
          dividers
        >
          <Stack direction={"row"} flexWrap={"wrap"} gap={2}>
            {state.omniPackageDetails?.map((item, index) => {
              return (
                <Card
                  key={index}
                  sx={(theme) => ({
                    p: "20px",
                    backgroundImage: theme.palette.background.cardGradient,
                    border: "1px solid var(--white)",
                    borderRadius: "20px",
                    display: "flex",
                    justifyContent: "center",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "10px",
                    flex: "1",
                    maxWidth: "100%",
                    minWidth: { xs: "200px", sm: "350px" },
                  })}
                >
                  <Stack alignItems={"center"} gap={"10px"}>
                    <Card
                      sx={(theme) => ({
                        p: "10px",
                        background: theme.palette.secondary.light,
                        borderRadius: "5px",
                        display: "flex",
                        justifyContent: "center",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "10px",
                        padding: "5px 10px",
                        color: theme.palette.common.black,
                        backgroundImage: "none",
                        fontWeight: "600",
                        fontSize: "16px",
                      })}
                    >
                      {item.PackageName}
                    </Card>
                    <img width={80} src="/icons/badge1.svg" alt="user" />
                    <Typography
                      color="secondary.light"
                      variant="h5"
                      fontWeight={600}
                    >
                      ${item.PackagePrice} {t("Common.One-Time")}
                    </Typography>
                    <Typography
                      color="secondary.text"
                      sx={{ fontSize: "12px" }}
                      variant="body1"
                      fontWeight={400}
                      lineHeight={1.7}
                    >
                      (${item.MonthlyPrice} {t("Common.Monthly")})
                    </Typography>
                    <Button
                      variant="contained"
                      sx={{ px: 3 }}
                      color="primary"
                      onClick={() => {
                        navigate(`${item.RedirectUrl}${state.refURL}`, { state: { upgrade: true } });
                      }}
                    >
                      {t("Common.Upgrade")}
                    </Button>
                  </Stack>
                </Card>
              );
            })}
          </Stack>
        </DialogContent>
      </Dialog>
      <CommonSnackBar snackObj={snack} closeSnack={closeSnack} buttonProp />
    </>
  );
}